'use client';

import { useState, useTransition } from 'react';
import Link from 'next/link';
import { Heart } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { analytics } from '@kit/analytics';
import {
  followOrganizationAction,
  unfollowOrganizationAction,
} from '@kit/community/server/actions';
import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kit/ui/dialog';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import pathsConfig from '~/config/paths.config';
import { DancerEvent } from '~/lib/analytics/dancer-events';

interface FollowButtonProps {
  organizationId: string;
  initialIsFollowing: boolean;
  /**
   * When false, clicking the button opens a sign-in prompt dialog
   * instead of calling the follow action. The action itself returns
   * "Not authenticated" for anonymous callers, but presenting that as
   * a toast on a public page is a poor UX — visitors get a clear modal
   * with a sign-in CTA preserving where they were on the org page.
   * Defaults to true for backward compatibility with the authenticated
   * `home/(user)/community/...` route where every viewer is signed in.
   */
  isAuthenticated?: boolean;
  /**
   * Path to return to after sign-in. Used as the `redirectTo` query
   * param on the sign-in link. Required only when `isAuthenticated` is
   * false.
   */
  signInRedirectTo?: string;
  /**
   * Extra classes for the button (sizing/layout only — the public org
   * page renders it h-10 full-width, Whop-style; the community surface
   * keeps the compact default).
   */
  className?: string;
}

export function FollowButton({
  organizationId,
  initialIsFollowing,
  isAuthenticated = true,
  signInRedirectTo,
  className,
}: FollowButtonProps) {
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [isPending, startTransition] = useTransition();
  const [signInOpen, setSignInOpen] = useState(false);
  const { t } = useTranslation();

  const handleFollow = () => {
    if (!isAuthenticated) {
      setSignInOpen(true);
      return;
    }

    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);

    startTransition(async () => {
      try {
        if (wasFollowing) {
          const result = await unfollowOrganizationAction(organizationId);
          if (!result.success) {
            setIsFollowing(true);
            toast.error(result.error || t('community:follow.unfollowFailed'));
          }
        } else {
          const result = await followOrganizationAction(organizationId);
          if (!result.success) {
            setIsFollowing(false);
            toast.error(result.error || t('community:follow.followFailed'));
          } else {
            // "Finding" activation signal (H2 candidate).
            void analytics.trackEvent(DancerEvent.OrgFollowed, {
              organization_id: organizationId,
            });
          }
        }
      } catch (error) {
        console.error('Failed to update follow status:', error);
        setIsFollowing(wasFollowing);
        toast.error(t('community:follow.genericError'));
      }
    });
  };

  const signInHref = signInRedirectTo
    ? `${pathsConfig.auth.signIn}?next=${encodeURIComponent(signInRedirectTo)}`
    : pathsConfig.auth.signIn;

  return (
    <>
      <Button
        onClick={handleFollow}
        disabled={isPending}
        variant={isFollowing ? 'outline' : 'default'}
        size="sm"
        className={cn('gap-2', className)}
        aria-label={
          isFollowing
            ? t('community:follow.unfollowOrganizationAriaLabel')
            : t('community:follow.followOrganizationAriaLabel')
        }
        aria-pressed={isFollowing}
      >
        <Heart
          className={`h-4 w-4 ${isFollowing ? 'fill-current' : ''}`}
          aria-hidden="true"
        />
        {isPending ? (
          <Trans i18nKey="common:loading.default" defaults="Loading..." />
        ) : isFollowing ? (
          <Trans i18nKey="community:profile.following" defaults="Following" />
        ) : (
          <Trans i18nKey="community:profile.follow" defaults="Follow" />
        )}
      </Button>

      <Dialog open={signInOpen} onOpenChange={setSignInOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Trans
                i18nKey="community:signInToFollow.title"
                defaults="Sign in to follow"
              />
            </DialogTitle>
            <DialogDescription>
              <Trans
                i18nKey="community:signInToFollow.description"
                defaults="Create a free account or sign in to follow this organization and stay up to date with their auditions, events, and posts."
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSignInOpen(false)}
              type="button"
            >
              <Trans i18nKey="common:cancel" defaults="Cancel" />
            </Button>
            <Button asChild>
              <Link href={signInHref}>
                <Trans
                  i18nKey="community:signInToFollow.cta"
                  defaults="Sign in"
                />
              </Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
